import React, { useEffect, useRef, useState } from 'react';
import { FileDown, Loader, MoreVertical } from 'lucide-react';
import ReasonDialog from '../../../common/ReasonDialog';
import { useToast } from '../../../common/Toast';
import { useCanPerform } from '../../../../hooks/useCanPerform';
import { describeApiError } from '../../../../utils/apiErrors';
import { getVisitNotePdf } from '../../../../services/patientmr/charting.service';
import { VISIT_NOTE_PURPOSE_MAX, VISIT_NOTE_PURPOSE_MIN, VisitNoteAudience } from '../../../../dtos/patientmr/visit.dto';

/** Open the 5-minute visit note link in a new tab (same pattern as the assessment PDF) */
export async function openVisitNote(patientId: string, visitId: string, audience: VisitNoteAudience = 'internal', purpose?: string): Promise<void> {
  const { url } = await getVisitNotePdf(patientId, visitId, { audience, purpose });
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Phase 18 M9: download state for the internal copy and the external copy (purpose dialog).
 * Callers gate the external copy on the Phase 14 export permission (`admin:export:patient`).
 */
export function useVisitNoteDownload(patientId: string | undefined) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const [externalFor, setExternalFor] = useState<string | null>(null);

  const download = async (visitId: string, audience: VisitNoteAudience = 'internal', purpose?: string) => {
    if (!patientId || !visitId) return;
    try {
      setBusy(true);
      await openVisitNote(patientId, visitId, audience, purpose);
      setExternalFor(null);
    } catch (error) {
      showToast(describeApiError(error, 'Could not prepare the visit note.'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const dialog = (
    <ReasonDialog
      open={externalFor !== null}
      title="External copy of the visit note"
      description="Entries marked internal are left out. The purpose is saved to the audit log; don't include clinical details."
      label="Purpose or recipient"
      confirmLabel="Download"
      minLength={VISIT_NOTE_PURPOSE_MIN}
      maxLength={VISIT_NOTE_PURPOSE_MAX}
      busy={busy}
      onConfirm={(purpose) => (externalFor ? download(externalFor, 'external', purpose) : undefined)}
      onCancel={() => setExternalFor(null)}
    />
  );

  return { busy, download, askExternal: (visitId: string) => setExternalFor(visitId), dialog };
}

export interface VisitNoteMenuProps {
  patientId: string;
  visitId: string;
}

/** Row menu for visit history: "Download visit note" and, with the export permission, "External copy…" */
export const VisitNoteMenu: React.FC<VisitNoteMenuProps> = ({ patientId, visitId }) => {
  const note = useVisitNoteDownload(patientId);
  const canExportExternal = useCanPerform('export:patient');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const item = 'block w-full min-h-[40px] px-3 text-left text-sm text-slate-800 hover:bg-slate-50';
  return (
    <div ref={boxRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label="Visit note" onClick={() => setOpen((o) => !o)} disabled={note.busy}
        className="inline-flex min-h-[32px] items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900">
        {note.busy ? <Loader size={14} className="animate-spin" /> : <MoreVertical size={14} aria-hidden="true" />}Visit note
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-48 rounded border border-slate-200 bg-white py-1 shadow-lg">
          <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); void note.download(visitId); }}>Download visit note</button>
          {canExportExternal && (
            <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); note.askExternal(visitId); }}>External copy…</button>
          )}
        </div>
      )}
      {note.dialog}
    </div>
  );
};

export interface DownloadVisitNoteButtonProps {
  patientId: string;
  visitId: string;
}

/** ChartIntegrityPanel button: the internal copy */
export const DownloadVisitNoteButton: React.FC<DownloadVisitNoteButtonProps> = ({ patientId, visitId }) => {
  const note = useVisitNoteDownload(patientId);
  return (
    <button type="button" onClick={() => note.download(visitId)} disabled={note.busy}
      className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50 min-h-[44px] md:min-h-0">
      {note.busy ? <Loader size={14} className="animate-spin" aria-hidden="true" /> : <FileDown size={14} aria-hidden="true" />} Download visit note
    </button>
  );
};
